import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Calendar.css';
import api from '../utils/api';

interface CalendarProps {
  residentId?: string;
  onDateSelect: (date: Date) => void;
  selectedDate: Date;
}

interface CalendarNote {
  id: string;
  date: string;
  hasNotes: boolean;
  noteCount: number;
}

interface CalendarAppointment {
  id: string;
  date: string;
  hasAppointments: boolean;
  appointmentCount: number;
  titles: string[];
}

interface OverviewCalendarData {
  date: string;
  totalCount: number;
  residents: Array<{
    residentId: number;
    residentName: string;
    count: number;
  }>;
}

// Format date to local YYYY-MM-DD string (avoiding UTC conversion)
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DiaryCalendar: React.FC<CalendarProps> = ({ residentId, onDateSelect, selectedDate }) => {
  const [noteDates, setNoteDates] = useState<CalendarNote[]>([]);
  const [appointmentDates, setAppointmentDates] = useState<CalendarAppointment[]>([]);
  const [overviewData, setOverviewData] = useState<OverviewCalendarData[]>([]);
  const [residentColors, setResidentColors] = useState<Map<number, string>>(new Map());

  // Memoize overview data index for O(1) lookups
  const overviewDataMap = useMemo(() => {
    const map = new Map<string, OverviewCalendarData>();
    overviewData.forEach(item => {
      map.set(item.date, item);
    });
    return map;
  }, [overviewData]);

  // Memoize unique residents for legend
  const getUniqueResidents = useMemo(() => {
    const residentsMap = new Map<number, string>();
    overviewData.forEach(dayData => {
      dayData.residents.forEach(resident => {
        if (!residentsMap.has(resident.residentId)) {
          residentsMap.set(resident.residentId, resident.residentName || 'Unknown');
        }
      });
    });
    return Array.from(residentsMap.entries()).map(([id, name]) => ({ id, name }));
  }, [overviewData]);

  const fetchNoteDates = useCallback(async () => {
    try {
      const response = await api.get(`/notes/calendar/${residentId}`);
      setNoteDates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching note dates:', error);
      setNoteDates([]);
    }
  }, [residentId]);

  const fetchAppointmentDates = useCallback(async () => {
    try {
      const response = await api.get(`/appointments/calendar/${residentId}`);
      setAppointmentDates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching appointment dates:', error);
      setAppointmentDates([]);
    }
  }, [residentId]);

  const fetchOverviewData = useCallback(async () => {
    try {
      // Get current month's first and last day
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 6, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 7, 0);
      
      const startDate = formatLocalDate(firstDay);
      const endDate = formatLocalDate(lastDay);
      
      const response = await api.get(`/appointments/calendar?startDate=${startDate}&endDate=${endDate}`);
      const data = response.data.data || [];
      setOverviewData(data);

      // Generate colors for each unique resident
      const colors = new Map<number, string>();
      const colorPalette = [
        '#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336',
        '#00BCD4', '#FFEB3B', '#795548', '#607D8B', '#E91E63'
      ];
      
      let colorIndex = 0;
      data.forEach((dayData: OverviewCalendarData) => {
        dayData.residents.forEach(resident => {
          if (!colors.has(resident.residentId)) {
            colors.set(resident.residentId, colorPalette[colorIndex % colorPalette.length]);
            colorIndex++;
          }
        });
      });
      
      setResidentColors(colors);
    } catch (error) {
      console.error('Error fetching overview data:', error);
      setOverviewData([]);
    }
  }, []);

  useEffect(() => {
    if (residentId) {
      fetchNoteDates();
      fetchAppointmentDates();
    } else {
      // Overview mode - fetch all appointments
      fetchOverviewData();
    }
  }, [residentId, fetchNoteDates, fetchAppointmentDates, fetchOverviewData]);

  const hasNotesOnDate = (date: Date): boolean => {
    const dateStr = formatLocalDate(date);
    return noteDates.some(noteDate => 
      noteDate.date.startsWith(dateStr) && noteDate.hasNotes
    );
  };

  const hasAppointmentsOnDate = (date: Date): boolean => {
    const dateStr = formatLocalDate(date);
    return appointmentDates.some(appointmentDate => 
      appointmentDate.date.startsWith(dateStr) && appointmentDate.hasAppointments
    );
  };

  const getNotesCountOnDate = (date: Date): number => {
    const dateStr = formatLocalDate(date);
    const noteDateInfo = noteDates.find(noteDate => 
      noteDate.date.startsWith(dateStr)
    );
    return noteDateInfo?.noteCount || 0;
  };

  const getAppointmentsCountOnDate = (date: Date): number => {
    const dateStr = formatLocalDate(date);
    const appointmentDateInfo = appointmentDates.find(appointmentDate => 
      appointmentDate.date.startsWith(dateStr)
    );
    return appointmentDateInfo?.appointmentCount || 0;
  };

  const getOverviewDataForDate = (date: Date): OverviewCalendarData | null => {
    const dateStr = formatLocalDate(date);
    return overviewDataMap.get(dateStr) || null;
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      // Overview mode - show multi-resident indicators
      if (!residentId) {
        const dayData = getOverviewDataForDate(date);
        if (dayData && dayData.totalCount > 0) {
          // Sort residents by count descending to show most active first
          const sortedResidents = [...dayData.residents].sort((a, b) => b.count - a.count);
          
          return (
            <div className="calendar-indicators overview-mode">
              <div className="overview-appointment-count">{dayData.totalCount}</div>
              <div className="resident-dots">
                {sortedResidents.slice(0, 3).map((resident, index) => (
                  <span 
                    key={resident.residentId}
                    className="resident-dot"
                    style={{ backgroundColor: residentColors.get(resident.residentId) || '#999' }}
                    title={`${resident.residentName || 'Unknown'}: ${resident.count} appointment${resident.count > 1 ? 's' : ''}`}
                  ></span>
                ))}
                {sortedResidents.length > 3 && (
                  <span className="more-residents">+{sortedResidents.length - 3}</span>
                )}
              </div>
            </div>
          );
        }
      } else {
        // Resident mode - show notes and appointments
        const noteCount = getNotesCountOnDate(date);
        const appointmentCount = getAppointmentsCountOnDate(date);
        
        if (noteCount > 0 || appointmentCount > 0) {
          return (
            <div className="calendar-indicators">
              {appointmentCount > 0 && (
                <div className="calendar-appointment-indicator">
                  <span className="appointment-dot"></span>
                  <span className="appointment-count">{appointmentCount}</span>
                </div>
              )}
              {noteCount > 0 && (
                <div className="calendar-note-indicator">
                  <span className="note-dot"></span>
                  <span className="note-count">{noteCount}</span>
                </div>
              )}
            </div>
          );
        }
      }
    }
    return null;
  };

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view === 'month') {
      const classes = [];
      
      // Overview mode
      if (!residentId) {
        const dayData = getOverviewDataForDate(date);
        if (dayData && dayData.totalCount > 0) {
          classes.push('has-overview-appointments');
        }
      } else {
        // Resident mode
        if (hasNotesOnDate(date)) {
          classes.push('has-notes');
        }

        if (hasAppointmentsOnDate(date)) {
          classes.push('has-appointments');
        }
      }
      
      if (date.toDateString() === selectedDate.toDateString()) {
        classes.push('selected-date');
      }
      
      return classes.join(' ');
    }
    return null;
  };


  return (
    <div className="diary-calendar">
      <div className="calendar-header">
        <h3>{residentId ? 'Diary Calendar' : 'All Appointments Calendar'}</h3>
        {residentId ? (
          <div className="calendar-legend">
            <div className="legend-item">
              <span className="legend-dot has-appointments"></span>
              <span>Appointments</span>
            </div>
            <div className="legend-item">
              <span className="legend-dot has-notes"></span>
              <span>Notes</span>
            </div>
          </div>
        ) : (
          <div className="calendar-legend overview-legend">
            <div className="legend-title">Residents:</div>
            <div className="legend-residents">
              {getUniqueResidents.slice(0, 10).map(resident => (
                <div key={resident.id} className="legend-item">
                  <span 
                    className="legend-dot" 
                    style={{ backgroundColor: residentColors.get(resident.id) || '#999' }}
                  ></span>
                  <span>{resident.name}</span>
                </div>
              ))}
              {getUniqueResidents.length > 10 && (
                <div className="legend-item">
                  <span>+{getUniqueResidents.length - 10} more</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      <Calendar
        onChange={(value) => onDateSelect(value as Date)}
        value={selectedDate}
        tileContent={tileContent}
        tileClassName={tileClassName}
        showNeighboringMonth={false}
      />
      
    </div>
  );
};

export default DiaryCalendar;